import React, { Component, Fragment, ReactNode } from 'react';

import AnimateHeight from 'react-animate-height';
import { injectIntl, InjectedIntl } from 'react-intl';

import Button from '@atlaskit/button/standard-button';
/* eslint frontbucket-patterns/no-old-dropdown-menu: "warn" */
import DropdownMenu, {
  DropdownItemGroup,
  DropdownItem,
} from '@atlaskit/dropdown-menu';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import messages from './dropdown-expander.i18n';
import * as styles from './dropdown-expander.style';

export type DropdownExpanderProps = {
  intl: InjectedIntl;
  children?: ReactNode;
  icon: JSX.Element;
  label: ReactNode;
  defaultCollapsed?: boolean;
  defaultClosed?: boolean;
  isCollapsible?: boolean;
  onChange?: (isCollapsed: boolean) => void;
  onClose?: () => void;
};

type DropdownExpanderState = {
  isCollapsed: boolean;
  isClosed: boolean;
};

export default injectIntl(
  class DropdownExpander extends Component<
    DropdownExpanderProps,
    DropdownExpanderState
  > {
    static defaultProps = {
      defaultCollapsed: false,
      defaultClosed: false,
      isCollapsible: true,
    };

    state: DropdownExpanderState = {
      isCollapsed: this.props.defaultCollapsed || false,
      isClosed: this.props.defaultClosed || false,
    };

    toggleCollapse = (): void => {
      const { isCollapsed } = this.state;

      if (this.props.onChange) {
        this.props.onChange(isCollapsed);
      }
      this.setState(() => ({ isCollapsed: !isCollapsed }));
    };

    handleClose = (): void => {
      const { isClosed } = this.state;
      if (!isClosed) {
        this.setState({ isClosed: true });
        if (this.props.onClose) {
          this.props.onClose();
        }
      }
    };

    renderHeading() {
      const { icon, label, intl, isCollapsible } = this.props;
      const { isCollapsed } = this.state;

      return (
        <Fragment>
          <styles.Icon>{icon}</styles.Icon>
          <Fragment>
            <styles.PanelHeadingLabel>{label}</styles.PanelHeadingLabel>
          </Fragment>
          {isCollapsible ? (
            <DropdownMenu
              triggerType="button"
              triggerButtonProps={{
                appearance: 'subtle',
                iconBefore: <MoreIcon label="" />,
              }}
              shouldFlip={false}
              position="bottom right"
            >
              <DropdownItemGroup>
                <DropdownItem onClick={this.toggleCollapse}>
                  {isCollapsed
                    ? intl.formatMessage(messages.expand)
                    : intl.formatMessage(messages.minimize)}
                </DropdownItem>
                <DropdownItem onClick={this.handleClose}>
                  {intl.formatMessage(messages.close)}
                </DropdownItem>
              </DropdownItemGroup>
            </DropdownMenu>
          ) : (
            <Button
              iconBefore={
                <CrossIcon
                  primaryColor={token('color.icon', colors.N800)}
                  label={intl.formatMessage(messages.close)}
                />
              }
              appearance="subtle"
              spacing="none"
              onClick={this.handleClose}
            />
          )}
        </Fragment>
      );
    }

    render() {
      const { isCollapsed } = this.state;

      return (
        <styles.Panel isCollapsed={isCollapsed}>
          <styles.PanelHeading isCollapsed={isCollapsed}>
            {this.renderHeading()}
          </styles.PanelHeading>

          <AnimateHeight
            duration={200}
            easing="linear"
            height={isCollapsed ? 0 : 'auto'}
          >
            <styles.PanelBody>{this.props.children}</styles.PanelBody>
          </AnimateHeight>
        </styles.Panel>
      );
    }
  }
);
